import { useQuery } from "@tanstack/react-query";

 import {
   getAnnualPremiumSales,
   getBestMonthsPremium,
   getBestWeeksPremium,
   getMonthlyPremiumSales,
  getDashboardOverview,
 } from "../services/services";
import type { DashboardOverviewResponse, DashboardPeriod } from "../types/overview";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";

// Helper to get current agency context for query keys
// CRITICAL: Include agency ID in query key so each agency has separate cached data
const getAgencyContext = () => {
  const agencyId = AgencyTokenManager.getCurrentAgencyId();
  return agencyId || 'no-agency';
};

 export const useGetAnnualPremiumSales = () => {
   return useQuery({
    queryKey: ["premiumSales", "annual"],
     queryFn: getAnnualPremiumSales,
     // 15s poll + 15s staleTime:
     //   • Sits on dashboard → auto-refreshes every 15s (new sales appear)
     //   • Flips to another tab <15s away → cache hit, no refetch on return
     //   • Flips to another tab >15s away → refetches on focus, shows latest
     // This was `staleTime: 0 + refetchOnMount: true` which fired 5 parallel
     // queries every tab switch regardless of how recent the data already was.
     staleTime: 15_000,
     refetchInterval: 15_000,
     refetchIntervalInBackground: false,
     refetchOnWindowFocus: true,
     retry: 1,
     retryDelay: 1000,
   });
 };

 export const useGetMonthlyPremiumSales = () => {
   return useQuery({
    queryKey: ["premiumSales", "monthly"],
     queryFn: getMonthlyPremiumSales,
     // 15s poll + 15s staleTime:
     //   • Sits on dashboard → auto-refreshes every 15s (new sales appear)
     //   • Flips to another tab <15s away → cache hit, no refetch on return
     //   • Flips to another tab >15s away → refetches on focus, shows latest
     // This was `staleTime: 0 + refetchOnMount: true` which fired 5 parallel
     // queries every tab switch regardless of how recent the data already was.
     staleTime: 15_000,
     refetchInterval: 15_000,
     refetchIntervalInBackground: false,
     refetchOnWindowFocus: true,
     retry: 1,
     retryDelay: 1000,
   });
 };

 export const useGetBestMonthsPremiumSales = () => {
   return useQuery({
    queryKey: ["premiumSales", "best-months"],
     queryFn: getBestMonthsPremium,
     // 15s poll + 15s staleTime:
     //   • Sits on dashboard → auto-refreshes every 15s (new sales appear)
     //   • Flips to another tab <15s away → cache hit, no refetch on return
     //   • Flips to another tab >15s away → refetches on focus, shows latest
     // This was `staleTime: 0 + refetchOnMount: true` which fired 5 parallel
     // queries every tab switch regardless of how recent the data already was.
     staleTime: 15_000,
     refetchInterval: 15_000,
     refetchIntervalInBackground: false,
     refetchOnWindowFocus: true,
     retry: 1,
     retryDelay: 1000,
   });
 };

 export const useGetBestWeeksPremiumSales = () => {
   return useQuery({
    queryKey: ["premiumSales", "best-weeks"],
     queryFn: getBestWeeksPremium,
     // 15s poll + 15s staleTime:
     //   • Sits on dashboard → auto-refreshes every 15s (new sales appear)
     //   • Flips to another tab <15s away → cache hit, no refetch on return
     //   • Flips to another tab >15s away → refetches on focus, shows latest
     // This was `staleTime: 0 + refetchOnMount: true` which fired 5 parallel
     // queries every tab switch regardless of how recent the data already was.
     staleTime: 15_000,
     refetchInterval: 15_000,
     refetchIntervalInBackground: false,
     refetchOnWindowFocus: true,
     retry: 1,
     retryDelay: 1000,
   });
 };

export const useGetDashboardOverview = (period?: DashboardPeriod, agencyId?: string, startDate?: string, endDate?: string) => {
  // Use passed agencyId if provided (from React state), otherwise fall back to AgencyTokenManager
  // This ensures the query key updates reactively when agency changes in UI
  const agencyContext = agencyId || getAgencyContext();

  return useQuery<DashboardOverviewResponse>({
    // CRITICAL: Include agency context in query key so each agency has separate cached data
    // This ensures switching between agencies shows different stats for each agency
    queryKey: ["dashboard", "overview", period || "all-time", agencyContext, startDate || "", endDate || ""],
    queryFn: () => getDashboardOverview(period, startDate, endDate),
    // Backend gets agency from x-agency-id header automatically
    // See note above — same 15s polling/stale pattern for dashboard overview.
    staleTime: 15_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: 1,
    retryDelay: 1000,
  });
};